import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { ApiCallError, callFunction } from '@/lib/api';
import { latestXdis16ExchangeId } from '@/lib/xdis/xdisView';
import { XdisViewer } from './XdisViewer';
import { Xdis21Form } from './Xdis21Form';
import { formatDanishDateTime } from '@/lib/danishDateTime';

// Spor A block 3, T3.4 (XDIS-EPJ 8.1/8.2): one timeline per EpisodeOfCareIdentifier for the
// patient: received XDIS16 (and updates), received XDIS91, sent XDIS21 versions,
// sent XDIS18/XDIS91 answers, and the receipts on each envelope. From an XDIS91 the
// user can open a new XDIS21 version (5.6).

type FlowItem = {
  exchangeId: string;
  kind: string;
  direction: 'in' | 'out';
  at: string;
  label: string;
  envelopeIdentifier: string | null;
  updated: boolean;
  receipts: { kind: string; at: string | null; refuseCode: string | null }[];
};
type FlowEpisode = { episodeOfCareIdentifier: string; items: FlowItem[] };
type Viewed = { exchangeId: string; kind: string; patientId: string | null; xml: string };

function formatTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : formatDanishDateTime(d);
}

export function XdisFlowPanel({
  clinicId,
  patientId,
  patientName,
  canRevealCpr = true,
}: {
  clinicId: string;
  patientId: string;
  patientName: string | null;
  canRevealCpr?: boolean;
}) {
  const { t } = useTranslation();
  const [viewed, setViewed] = useState<Viewed | null>(null);
  const [viewError, setViewError] = useState<string | null>(null);
  const [planFor, setPlanFor] = useState<{ sourceExchangeId: string; carePlanExchangeId: string | null } | null>(null);
  const flow = useQuery({
    queryKey: ['communicate-xdis-flow', clinicId, patientId],
    staleTime: 30_000,
    queryFn: () => callFunction<Record<string, unknown>, { episodes: FlowEpisode[] }>('communicate-xdis', { action: 'flow', clinic_id: clinicId, patient_id: patientId }),
  });

  async function onView(item: FlowItem) {
    setViewError(null);
    try {
      setViewed(await callFunction<Record<string, unknown>, Viewed>('communicate-xdis', { action: 'view', clinic_id: clinicId, exchange_id: item.exchangeId }));
    } catch (err) {
      setViewError(err instanceof ApiCallError ? err.message : t('patients.xdis.view_failed', 'Rapporten kunne ikke vises.'));
    }
  }

  const episodes = flow.data?.episodes ?? [];
  if (planFor) {
    return (
      <section className="space-y-3 rounded-md border border-ds-line p-4" data-testid="xdis-flow">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">{t('patients.xdis.flow_title', 'Hjemmeplejeforløb (XDIS16/XDIS21/XDIS18/XDIS91)')}</h3>
          <Button type="button" size="sm" variant="secondary" onClick={() => void flow.refetch()} disabled={flow.isFetching}>
            {flow.isFetching ? t('medcom.correspondence.refreshing', 'Opdaterer…') : t('medcom.correspondence.refresh', 'Opdatér')}
          </Button>
        </div>
        <Xdis21Form
          clinicId={clinicId}
          patientId={patientId}
          patientName={patientName}
          sourceExchangeId={planFor.sourceExchangeId}
          carePlanExchangeId={planFor.carePlanExchangeId}
          canRevealCpr={canRevealCpr}
          onClose={() => setPlanFor(null)}
        />
      </section>
    );
  }
  return (
    <section className="space-y-3 rounded-md border border-ds-line p-4" data-testid="xdis-flow">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{t('patients.xdis.flow_title', 'Hjemmeplejeforløb (XDIS16/XDIS21/XDIS18/XDIS91)')}</h3>
        <Button type="button" size="sm" variant="secondary" onClick={() => void flow.refetch()} disabled={flow.isFetching}>
          {flow.isFetching ? t('medcom.correspondence.refreshing', 'Opdaterer…') : t('medcom.correspondence.refresh', 'Opdatér')}
        </Button>
      </div>
      {flow.error && (
        <p role="alert" className="text-sm text-ds-red-ink">
          {flow.error instanceof ApiCallError ? flow.error.message : t('patients.xdis.flow_failed', 'Forløbet kunne ikke hentes.')}
        </p>
      )}
      {flow.isSuccess && episodes.length === 0 && <p className="text-sm text-ds-muted">{t('patients.xdis.flow_empty', 'Ingen forløb med hjemmeplejen.')}</p>}
      {episodes.map((ep) => (
        <div key={ep.episodeOfCareIdentifier} className="space-y-1" data-testid="xdis-flow-episode">
          <div className="text-xs font-medium text-ds-muted">
            {t('patients.xdis.episode', 'Forløb')}: <span className="font-mono">{ep.episodeOfCareIdentifier}</span>
          </div>
          <ol className="space-y-1 border-l-2 border-ds-line pl-3">
            {ep.items.map((item) => (
              <li key={`${item.direction}:${item.exchangeId}`} className="text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-ds-muted">{formatTime(item.at)}</span>
                  <span className={item.updated ? 'font-medium text-ds-amber-ink ' : 'font-medium'}>{item.label}</span>
                  {(item.kind === 'XDIS16' || item.kind === 'XDIS18' || item.kind === 'XDIS21') && (
                    <Button type="button" size="sm" variant="secondary" onClick={() => void onView(item)}>
                      {t('patients.xdis.view', 'Vis')}
                    </Button>
                  )}
                  {item.kind === 'XDIS91' && (() => {
                    const sourceExchangeId = latestXdis16ExchangeId(ep.items);
                    const latestPlan = [...ep.items].reverse().find((i) => i.kind === 'XDIS21');
                    if (!sourceExchangeId) return null;
                    return (
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => setPlanFor({ sourceExchangeId, carePlanExchangeId: latestPlan?.exchangeId ?? null })}
                        data-testid="xdis21-from-correspondence"
                      >
                        {t('patients.xdis.from_correspondence', 'Indfør i plejeforløbsplan')}
                      </Button>
                    );
                  })()}
                </div>
                {item.receipts.length > 0 && (
                  <ul className="ml-4 text-xs text-ds-muted">
                    {item.receipts.map((r, i) => (
                      <li key={i}>
                        {r.kind}
                        {r.refuseCode ? ` (${r.refuseCode})` : ''} {formatTime(r.at)}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ol>
        </div>
      ))}
      {viewError && <p role="alert" className="text-sm text-ds-red-ink">{viewError}</p>}
      {viewed && (
        <div className="space-y-2">
          <div className="flex justify-end">
            <Button type="button" size="sm" variant="secondary" onClick={() => setViewed(null)}>
              {t('patients.xdis.close', 'Luk')}
            </Button>
          </div>
          <XdisViewer xml={viewed.xml} kind={viewed.kind} patientId={viewed.patientId} patientName={patientName} canRevealCpr={canRevealCpr} />
        </div>
      )}
    </section>
  );
}
